import { Link } from "react-router-dom";
import {
  FolderOpen,
  PanelRightOpen,
  PanelRightClose,
  Link2,
  MessageSquare,
  Maximize2,
  Sun,
  Moon,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  LogOut,
  LogIn,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AppLogo } from "@/components/AppLogo";
import { USER_STATUS } from "@/contexts/AuthContext";

export function AppHeader({
  user,
  isAuthenticated,
  userStatusInfo,
  status,
  voiceStatusInfo,
  layoutMode,
  ideasCount,
  newViewsCount,
  unreadMessages,
  isDark,
  onToggleLayout,
  onOpenIdeas,
  onOpenShares,
  onOpenFocus,
  onToggleTheme,
  onLogout,
  onOpenMessages,
  onStartTutorial,
}) {
  return (
    <header
      className="flex items-center justify-between px-4 py-2.5 md:px-8 lg:px-12 md:py-3"
      style={{ borderBottom: "1px solid var(--vi-border)" }}
      data-testid="app-header"
    >
      <div className="flex items-center">
        <AppLogo size="default" showText className="hidden sm:flex" />
        <AppLogo size="default" showText={false} className="sm:hidden" />
      </div>

      <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto scrollbar-hide">
        {/* Ideas Panel Trigger (panel mode only) */}
        {layoutMode === "panel" && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 bg-white/5 border border-white/10 hover:bg-white/10"
            onClick={onOpenIdeas}
            data-testid="open-ideas-panel"
          >
            <FolderOpen className="w-4 h-4" />
            <span className="hidden sm:inline">Minhas Ideias</span>
            {ideasCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-indigo-500/20 text-indigo-400">
                {ideasCount}
              </span>
            )}
          </Button>
        )}

        {/* Layout Toggle */}
        {isAuthenticated && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleLayout}
                className={`gap-2 border transition-colors ${
                  layoutMode === "split"
                    ? "bg-indigo-500/15 border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/25"
                    : "bg-white/5 border-white/10 hover:bg-white/10"
                }`}
                data-testid="layout-toggle-button"
              >
                {layoutMode === "split" ? (
                  <PanelRightClose className="w-4 h-4" />
                ) : (
                  <PanelRightOpen className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">
                  {layoutMode === "split" ? "Esconder" : "Fixar"}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {layoutMode === "split"
                ? "Esconder painel de ideias"
                : "Fixar painel de ideias ao lado"}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Shared Links Panel */}
        {isAuthenticated && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 bg-white/5 border border-white/10 hover:bg-white/10 relative"
            onClick={onOpenShares}
            data-testid="open-shares-panel"
          >
            <Link2 className="w-4 h-4" />
            <span className="hidden sm:inline">Links</span>
            {newViewsCount > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full bg-rose-500 text-white px-1"
                data-testid="new-views-badge"
              >
                {newViewsCount > 99 ? "99+" : newViewsCount}
              </span>
            )}
          </Button>
        )}

        {/* Messages Button */}
        {isAuthenticated && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onOpenMessages}
                className="gap-2 bg-white/5 border border-white/10 hover:bg-white/10 relative"
                data-testid="open-messages-button"
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline">Mensagens</span>
                {unreadMessages > 0 && (
                  <span
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full bg-indigo-500 text-white px-1"
                    data-testid="unread-messages-badge"
                  >
                    {unreadMessages > 99 ? "99+" : unreadMessages}
                  </span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Mensagens internas</TooltipContent>
          </Tooltip>
        )}

        {/* Focus Mode Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenFocus}
              className="gap-2 bg-white/5 border border-white/10 hover:bg-white/10"
              data-testid="focus-mode-button"
            >
              <Maximize2 className="w-4 h-4" />
              <span className="hidden sm:inline">Foco</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Modo foco — escreva sem distrações</TooltipContent>
        </Tooltip>

        {/* Tutorial Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onStartTutorial}
              className="gap-1.5 text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50"
              style={{ border: "1px solid var(--vi-border-accent)" }}
              data-testid="tutorial-button"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Tutorial</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Ver tutorial da plataforma</TooltipContent>
        </Tooltip>

        {/* Theme Toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleTheme}
              className="h-9 w-9 rounded-full"
              style={{
                background: "var(--vi-input-bg)",
                border: "1px solid var(--vi-border-accent)",
              }}
              data-testid="theme-toggle-button"
            >
              {isDark ? (
                <Sun className="w-4 h-4 text-amber-400" />
              ) : (
                <Moon className="w-4 h-4 text-indigo-500" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isDark ? "Modo claro" : "Modo escuro"}
          </TooltipContent>
        </Tooltip>

        {/* User Status Badge (Trial/Active) */}
        {isAuthenticated && userStatusInfo && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={`hidden sm:flex flex-col items-end gap-0.5 px-3 py-1.5 rounded-xl text-xs font-medium border ${userStatusInfo.color}`}
                data-testid="user-status-badge"
              >
                <div className="flex items-center gap-1.5">
                  {userStatusInfo.icon === "clock" && (
                    <Clock className="w-3 h-3" />
                  )}
                  {userStatusInfo.icon === "check" && (
                    <CheckCircle className="w-3 h-3" />
                  )}
                  {userStatusInfo.icon === "x" && (
                    <AlertCircle className="w-3 h-3" />
                  )}
                  <span>{userStatusInfo.label}</span>
                </div>
                {userStatusInfo.sublabel && (
                  <span className="text-[10px] opacity-70">
                    {userStatusInfo.sublabel}
                  </span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-center">
                <p>{userStatusInfo.label}</p>
                {userStatusInfo.sublabel && (
                  <p className="text-xs opacity-70">
                    {userStatusInfo.sublabel}
                  </p>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        )}

        {/* User Menu / Login Button */}
        {isAuthenticated ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 bg-white/5 border border-white/10 hover:bg-white/10"
                data-testid="user-menu-button"
              >
                <div className="w-6 h-6 rounded-full bg-indigo-500/30 flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-indigo-400" />
                </div>
                <span
                  className="hidden sm:inline max-w-[100px] truncate"
                  style={{ color: "var(--vi-text-soft)" }}
                >
                  {user?.name}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56"
              style={{
                background: "var(--vi-card-solid)",
                border: "1px solid var(--vi-border-accent)",
              }}
            >
              <div className="px-2 py-1.5">
                <p
                  className="text-sm font-medium"
                  style={{ color: "var(--vi-text)" }}
                >
                  {user?.name}
                </p>
                <p
                  className="text-xs truncate"
                  style={{ color: "var(--vi-text-muted)" }}
                >
                  {user?.email}
                </p>
              </div>
              {user?.status === USER_STATUS.ATIVO && user?.plan_name && (
                <>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <div className="px-2 py-1.5">
                    <p className="text-xs text-muted-foreground">
                      Plano:{" "}
                      <span className="text-emerald-400">
                        {user.plan_name}
                      </span>
                    </p>
                    {user.subscription_end && (
                      <p className="text-xs text-muted-foreground">
                        Expira em:{" "}
                        {new Date(user.subscription_end).toLocaleDateString(
                          "pt-BR"
                        )}
                      </p>
                    )}
                  </div>
                </>
              )}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onClick={onLogout}
                className="text-rose-400 focus:text-rose-400 focus:bg-rose-500/10 cursor-pointer"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link to="/login">
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 bg-indigo-500/10 border border-indigo-500/30 hover:bg-indigo-500/20 text-indigo-400"
              data-testid="login-button"
            >
              <LogIn className="w-4 h-4" />
              <span className="hidden sm:inline">Entrar</span>
            </Button>
          </Link>
        )}

        {/* Status Badge */}
        <div
          className={`px-3 py-1.5 md:px-4 md:py-2 rounded-full ${voiceStatusInfo.bgColor} ${
            status === "recording" ? "status-recording" : ""
          } transition-all duration-200`}
          data-testid="status-badge"
        >
          <span
            className={`text-xs md:text-sm font-medium ${voiceStatusInfo.color}`}
          >
            {voiceStatusInfo.text}
          </span>
        </div>
      </div>
    </header>
  );
}
